import { PersonOutline, BusinessOutlined, DeleteOutline, ExpandMore, ExpandLess, AddOutlined } from '@mui/icons-material';
import { useState } from 'react';

import type { BeneficialOwner, CreateBeneficialOwnerDto } from '@/types/adminOnboarding';

import { BeneficialOwnerForm } from './BeneficialOwnerForm';
import { DocumentUploadCard } from './DocumentUploadCard';

interface BeneficialOwnerCardProps {
  ubo: BeneficialOwner;
  onDelete: (id: string) => void;
  onAddChild?: (parentId: string, data: CreateBeneficialOwnerDto) => void;
  onUploadDoc: (uboId: string, documentType: string, file: File) => Promise<void> | void;
  onDeleteDoc?: (documentId: string) => void;
  isDeleting?: boolean;
  depth?: number;
}

const UBO_DOCS_INDIVIDUAL = [
  { type: 'identificacion', label: 'Identificación oficial' },
  { type: 'constancia_csf', label: 'Constancia de Situación Fiscal' },
  { type: 'comprobante_domicilio', label: 'Comprobante de domicilio' },
];

const UBO_DOCS_COMPANY = [
  { type: 'acta_constitutiva', label: 'Acta Constitutiva' },
  { type: 'constancia_csf', label: 'Constancia de Situación Fiscal' },
  { type: 'registro_publico_comercio', label: 'Registro Público de Comercio' },
  { type: 'poderes', label: 'Poderes notariales' },
  { type: 'comprobante_domicilio', label: 'Comprobante de domicilio' },
];

export const BeneficialOwnerCard: React.FC<BeneficialOwnerCardProps> = ({
  ubo,
  onDelete,
  onAddChild,
  onUploadDoc,
  onDeleteDoc,
  isDeleting = false,
  depth = 0,
}) => {
  const [showDocs, setShowDocs] = useState(false);
  const [showAddChild, setShowAddChild] = useState(false);
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null);

  const docDefs = ubo.type === 'individual' ? UBO_DOCS_INDIVIDUAL : UBO_DOCS_COMPANY;

  const getUploadedDoc = (docType: string) =>
    ubo.documents?.find(d => d.documentType === docType);

  const handleUpload = async (docType: string, file: File) => {
    setUploadingDocType(docType);
    try {
      await onUploadDoc(ubo.id, docType, file);
    } finally {
      setUploadingDocType(null);
    }
  };

  return (
    <div className={`border border-gray-200 rounded-lg ${depth > 0 ? 'ml-6 mt-2' : 'mb-3'}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {ubo.type === 'company' ? (
              <BusinessOutlined className="text-blue-500 flex-shrink-0" fontSize="small" />
            ) : (
              <PersonOutline className="text-gray-500 flex-shrink-0" fontSize="small" />
            )}
            <div className="min-w-0">
              <p className="font-medium text-gray-900 text-sm truncate">{ubo.name}</p>
              <p className="text-xs text-gray-500">
                {ubo.ownershipPercentage}% · RFC: {ubo.rfc}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setShowDocs(v => !v)}
              className="p-1.5 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="Ver documentos"
            >
              {showDocs ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </button>
            <button
              onClick={() => onDelete(ubo.id)}
              disabled={isDeleting}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
              title="Eliminar"
            >
              <DeleteOutline fontSize="small" />
            </button>
          </div>
        </div>

        {showDocs && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {docDefs.map(({ type, label }) => (
              <DocumentUploadCard
                key={type}
                label={label}
                documentType={type}
                uploadedFile={getUploadedDoc(type)}
                onUpload={(docType, file) => void handleUpload(docType, file)}
                onDelete={onDeleteDoc}
                uploadingDocType={uploadingDocType}
              />
            ))}
          </div>
        )}

        {ubo.type === 'company' && onAddChild && (
          <div className="mt-3">
            {!showAddChild ? (
              <button
                onClick={() => setShowAddChild(true)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 transition-colors"
              >
                <AddOutlined fontSize="small" />
                Agregar persona física bajo esta empresa
              </button>
            ) : (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700 mb-3">
                  Nueva persona física (bajo {ubo.name})
                </p>
                <BeneficialOwnerForm
                  defaultType="individual"
                  parentId={ubo.id}
                  onSubmit={data => {
                    onAddChild(ubo.id, data);
                    setShowAddChild(false);
                  }}
                  onCancel={() => setShowAddChild(false)}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {ubo.children && ubo.children.length > 0 && (
        <div className="px-4 pb-4">
          {ubo.children.map(child => (
            <BeneficialOwnerCard
              key={child.id}
              ubo={child}
              onDelete={onDelete}
              onUploadDoc={onUploadDoc}
              onDeleteDoc={onDeleteDoc}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};
